import { Box, Button, Center, Icon, Tag, Text } from "@chakra-ui/react";
import { useState } from "react";
import { FiLayers, FiPlus } from "react-icons/fi";
import { ListPageHeader, listHeaderRow } from "../../common/components/ListPageHeader";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Currency,
  type FrameworkAgreementDto,
  FrameworkAgreementStatus,
  SortDirection,
  useListFrameworkAgreementsQuery,
} from "../../autogen/bff-api";
import { useLoggedInWithOrgContextState } from "../../common/auth/useLoggedInWithOrgContextState";
import { offersContractCreation } from "../../common/departments/eventCreationDepartments";
import { EntityTable, EntityTableColumn } from "../../common/entity-table/EntityTable";
import { FilterBar } from "../../common/entity-table/FilterBar";
import { displaySum } from "../../common/formatting/displaySum";
import { useDisplayDate } from "../../common/formatting/useDisplayDate";
import { urls } from "../../urls";
import { Explanation } from "../../common/support/Explanation/Explanation";
import { RegisterFrameworkAgreementModal } from "./RegisterFrameworkAgreementModal";

export const FrameworkAgreementsList = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const displayDate = useDisplayDate();
  const { data, isLoading, isError, refetch } = useListFrameworkAgreementsQuery({});
  const [hiddenColumns, setHiddenColumns] = useState<string[]>([]);
  const [sort, setSort] = useState<{ key: string; direction: SortDirection }>();
  const [isRegistering, setRegistering] = useState(false);
  const { selectedOrg } = useLoggedInWithOrgContextState();
  const registerRefusal = selectedOrg.subscription?.hasContractAccess !== false ? undefined : t("NotInPlan");
  const mayRegister = offersContractCreation(selectedOrg);

  const [searchParams, setSearchParams] = useSearchParams();
  const filters = filtersIn(searchParams);
  const setFilter = (key: FilterParam, value?: string) =>
    setSearchParams(
      (params) => {
        if (value) params.set(key, value);
        else params.delete(key);
        return params;
      },
      { replace: true },
    );
  const resetFilters = () =>
    setSearchParams(
      (params) => {
        FILTER_PARAMS.forEach((key) => params.delete(key));
        return params;
      },
      { replace: true },
    );

  const isFiltered = FILTER_PARAMS.some((key) => searchParams.get(key));
  const frameworkAgreements = data?.frameworkAgreements ?? [];
  const sortValues: Record<string, (framework: FrameworkAgreementDto) => string | number | null> = {
    title: (framework) => framework.title,
    status: (framework) => t(framework.status),
    suppliers: (framework) => framework.summary.supplierCount,
    miniCompetitions: (framework) => framework.summary.miniCompetitionCount,
    calledOff: (framework) => (framework.summary.calledOffValue ? Number(framework.summary.calledOffValue) : null),
    expires: (framework) => framework.endDate ?? null,
  };
  const shown = sortedBy(
    frameworkAgreements.filter((framework) => matches(framework, filters)),
    sort && sortValues[sort.key],
    sort?.direction,
  );
  const statusOption = (status: FrameworkAgreementStatus) => ({ value: status, label: t(status) });

  const listColumns: EntityTableColumn[] = [
    {
      key: "title",
      label: t("Title"),
      width: "320px",
      alwaysShown: true,
    },
    {
      key: "status",
      label: t("Status"),
      multiSelectConfig: {
        options: Object.values(FrameworkAgreementStatus).map(statusOption),
        selectedValues: filters.statuses.map(statusOption),
        onSelect: (values) => setFilter("status", values.map((value) => value.value).join(",")),
      },
    },
    { key: "suppliers", label: t("Suppliers") },
    { key: "miniCompetitions", label: t("Mini-competitions") },
    { key: "calledOff", label: t("Called off so far") },
    {
      key: "expires",
      label: t("Expires"),
      dateRangeConfig: {
        fromDate: filters.expiresFrom,
        toDate: filters.expiresTo,
        onFromDateChange: (date) => setFilter("expiresFrom", date),
        onToDateChange: (date) => setFilter("expiresTo", date),
      },
    },
  ];
  const columns = listColumns.map((column) => ({
    ...column,
    hidden: hiddenColumns.includes(column.key),
    onSort: (direction: SortDirection) => setSort(direction ? { key: column.key, direction } : undefined),
    currentSortDirection: sort?.key === column.key ? sort.direction : undefined,
  }));

  const rows = shown.map((framework) => [
    <Button
      key="title"
      variant="link"
      color="smText"
      fontWeight="semibold"
      textAlign="left"
      whiteSpace="normal"
      onClick={() => navigate(urls.app.frameworkAgreements.view.go(framework.id))}
    >
      {framework.title}
    </Button>,
    <Tag key="status" size="sm" colorScheme={framework.status === FrameworkAgreementStatus.Active ? "teal" : "gray"}>
      {t(framework.status)}
    </Tag>,
    <Text key="suppliers">{framework.summary.supplierCount}</Text>,
    <Text key="miniCompetitions">{framework.summary.miniCompetitionCount}</Text>,
    <Text key="calledOff">
      {framework.summary.calledOffValue
        ? displaySum({
            value: Number(framework.summary.calledOffValue),
            currency: framework.ceilingValue?.currency ?? Currency.Nok,
          })
        : "-"}
    </Text>,
    <Text key="expires" color="smMuted">
      {framework.endDate ? displayDate(framework.endDate) : "-"}
    </Text>,
  ]);

  return (
    <Box>
      <ListPageHeader
        heading={t("Framework agreements")}
        headingColor="smText"
        {...listHeaderRow}
        mb="5"
        data-testid="list-header"
        actions={
          mayRegister && (
            <Explanation text={registerRefusal} enabled={!!registerRefusal} shouldWrapChildren>
              <Button
                colorScheme="teal"
                leftIcon={<FiPlus />}
                onClick={() => setRegistering(true)}
                isDisabled={!!registerRefusal}
              >
                {t("Register framework agreement")}
              </Button>
            </Explanation>
          )
        }
      />
      <Box px="2">
        {isRegistering && <RegisterFrameworkAgreementModal isOpen onClose={() => setRegistering(false)} />}
        {!isLoading && !isError && frameworkAgreements.length === 0 ? (
          <Center
            py="16"
            px="6"
            bg="smBackground"
            rounded="xl"
            border="1px solid"
            borderColor="smBorder"
            flexDirection="column"
            gap="3"
            color="smMuted"
          >
            <Icon as={FiLayers} boxSize="28px" />
            {data?.someWithheld ? (
              <Text fontWeight="medium" color="smText" maxW="440px" textAlign="center">
                {t(
                  "You don't have access to your organization's framework agreements. Ask an administrator in your organization for access.",
                )}
              </Text>
            ) : (
              <>
                <Text fontWeight="medium" color="smText">
                  {t("No framework agreements have been created yet")}
                </Text>
                <Text fontSize="sm" maxW="440px" textAlign="center">
                  {registerRefusal ??
                    t(
                      "You create one by awarding a competition where you chose a framework agreement, or by registering one concluded outside SourceMagnet.",
                    )}
                </Text>
              </>
            )}
          </Center>
        ) : (
          <Box bg="smBackground" rounded="xl" border="1px solid" borderColor="smBorder" p="4">
            <FilterBar
              columns={columns}
              onResetAll={resetFilters}
              countText={
                data
                  ? t("{{shown}} of {{count}} framework agreements", {
                      shown: shown.length,
                      count: frameworkAgreements.length,
                    })
                  : null
              }
              search={{
                value: filters.title ?? "",
                onChange: (value) => setFilter("title", value.trim() ? value : undefined),
                label: String(t("Search the table")),
                tooltip: String(t("Searches Title")),
              }}
            />
            <EntityTable
              emptyText={t("No framework agreements have been created yet")}
              noMatchText={t("No framework agreements match your search or filters.")}
              loadError={isError ? { message: t("Could not load framework agreements."), onRetry: () => void refetch() } : undefined}
              columns={columns}
              rows={rows}
              isLoading={isLoading}
              limit={Math.max(shown.length, 5)}
              hasActiveFilters={isFiltered}
              onReset={resetFilters}
              skip={0}
              setSkip={() => undefined}
              onShowColumn={(key) => setHiddenColumns((hidden) => hidden.filter((k) => k !== key))}
              onHideColumn={(key) => setHiddenColumns((hidden) => [...hidden, key])}
              onShowAllColumns={() => setHiddenColumns([])}
              onResetColumns={() => setHiddenColumns([])}
              hasColumnChanges={hiddenColumns.length > 0}
            />
          </Box>
        )}
      </Box>
    </Box>
  );
};

const FILTER_PARAMS = ["title", "status", "expiresFrom", "expiresTo"] as const;
type FilterParam = (typeof FILTER_PARAMS)[number];

interface Filters {
  title?: string;
  statuses: FrameworkAgreementStatus[];
  expiresFrom?: string;
  expiresTo?: string;
}

const filtersIn = (params: URLSearchParams): Filters => {
  const chosen = (params.get("status") ?? "").split(",");
  return {
    title: params.get("title") ?? undefined,
    statuses: Object.values(FrameworkAgreementStatus).filter((status) => chosen.includes(status)),
    expiresFrom: params.get("expiresFrom") ?? undefined,
    expiresTo: params.get("expiresTo") ?? undefined,
  };
};

const matches = (framework: FrameworkAgreementDto, filters: Filters): boolean => {
  const term = filters.title?.trim().toLowerCase();
  if (term && !framework.title.toLowerCase().includes(term)) return false;
  if (filters.statuses.length > 0 && !filters.statuses.includes(framework.status)) return false;
  if (filters.expiresFrom && !(framework.endDate && framework.endDate >= filters.expiresFrom)) return false;
  if (filters.expiresTo && !(framework.endDate && framework.endDate <= filters.expiresTo)) return false;
  return true;
};

const sortedBy = <T,>(
  items: T[],
  valueOf: ((item: T) => string | number | null) | undefined,
  direction: SortDirection | undefined,
): T[] => {
  if (!valueOf || !direction) return items;
  const sign = direction === SortDirection.Asc ? 1 : -1;
  return [...items].sort((a, b) => {
    const left = valueOf(a);
    const right = valueOf(b);
    if (left === right) return 0;
    if (left === null) return 1;
    if (right === null) return -1;
    if (typeof left === "number" && typeof right === "number") return sign * (left - right);
    return sign * String(left).localeCompare(String(right), "nb");
  });
};
